import React, { useState } from 'react';
import { Plus, Trash2, ChevronDown, ChevronUp, Check, Phone, Star, Users } from 'lucide-react';

const DEFAULT_TASKS = [
  { category: 'Ceremony', items: ['Ringene', 'Vigslingsdokumenter', 'Program bookletr', 'Confetti/risposer', 'Blomster til prosesjon'] },
  { category: 'Dekor & Oppsett', items: ['Welcome sign', 'Borddekorasjon', 'Lighttenning', 'Bordnummer', 'Place cards', 'Menysigns', 'Bakerst', 'QR sign'] },
  { category: 'Mat & Drikke', items: ['Bryllupskake-transport', 'Kakestativ/-kniv', 'Signaturcocktail-ingredienser', 'Ice/cooling'] },
  { category: 'Personlig', items: ['Wedding dress/antrekk', 'Makeup/hair tools', 'Emergency kit', 'Bridal bouquet', 'Boutonnière'] },
  { category: 'Guests & Fest', items: ['Guest book + penn', 'Gavetable/kasse', 'Photobooth-rekvisitter', 'Confettikanoner', 'Music playlist on USB'] },
  { category: 'Child', items: ['Childeaktiviteter', 'Childemeny/snacks', 'Childestoler'] },
  { category: 'Etter festen', items: ['Gaver & kort hjem', 'Dekor / blomster hjem', 'Clothing/personlige eiendeler', 'Rester av kake/mat'] },
  { category: 'Vendors', items: ['Vendorkontakt (day-of)', 'Final task / tips', 'Keys/venue access'] },
];

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Not assigned', color: 'bg-stone-100 text-stone-500' },
  { value: 'assigned', label: 'Assigned', color: 'bg-blue-100 text-blue-600' },
  { value: 'done', label: 'Completed', color: 'bg-green-100 text-green-600' },
];

const defaultTask = () => ({
  id: `task-${Date.now()}-${Math.random().toString(36).slice(2, 5)}`,
  task: '', responsible: '', phone: '', time: '', status: 'pending', notes: '', category: 'Other',
});

const Inp = ({ label, value, onChange, placeholder, type = 'text', className = '' }) => (
  <div className={className}>
    <label className="text-[9px] font-sans font-semibold text-stone-500 uppercase tracking-wide mb-0.5 block">{label}</label>
    <input type={type} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      className="w-full text-xs font-sans px-2.5 py-1.5 border border-stone-200 rounded-lg bg-white focus:border-primary focus:outline-none" />
  </div>
);

export const ResponsibilityTracker = ({ responsibilities, onChange }) => {
  const [expanded, setExpanded] = useState(null);
  const [showGenerate, setShowGenerate] = useState(false);

  const tasks = responsibilities?.tasks || [];
  const dayOfContact = responsibilities?.dayOfContact || {};

  const updateTasks = (list) => onChange({ ...responsibilities, tasks: list });
  const updateDayOfContact = (field, value) => onChange({
    ...responsibilities, dayOfContact: { ...dayOfContact, [field]: value },
  });

  const addTask = () => { updateTasks([...tasks, defaultTask()]); setExpanded(tasks.length); };
  const removeTask = (idx) => updateTasks(tasks.filter((_, i) => i !== idx));
  const updateTask = (idx, field, value) => {
    const updated = [...tasks];
    updated[idx] = { ...updated[idx], [field]: value };
    updateTasks(updated);
  };

  const generateDefaultTasks = () => {
    const generated = [];
    DEFAULT_TASKS.forEach(cat => {
      cat.items.forEach(item => {
        generated.push({
          ...defaultTask(),
          task: item,
          category: cat.category,
        });
      });
    });
    updateTasks(generated);
    setShowGenerate(false);
  };

  const byCategory = {};
  tasks.forEach((t, idx) => {
    const cat = t.category || 'Other';
    if (!byCategory[cat]) byCategory[cat] = [];
    byCategory[cat].push({ ...t, _idx: idx });
  });

  const completedCount = tasks.filter(t => t.status === 'done').length;
  const assignedCount = tasks.filter(t => t.status === 'assigned').length;

  return (
    <div data-testid="responsibility-tracker" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-sans font-semibold text-stone-800 text-sm">Responsibility tracker</h3>
          <p className="text-xs font-sans text-stone-500 mt-0.5">Who is responsible for what on the wedding day</p>
        </div>
        {tasks.length === 0 && (
          <button type="button" onClick={generateDefaultTasks} data-testid="generate-tasks-btn"
            className="bg-primary hover:bg-primary/90 text-white rounded-full px-4 py-2 text-[10px] font-sans font-semibold transition-colors">
            Auto-generer oppgaver
          </button>
        )}
      </div>

      {/* Day-of Point of Contact */}
      <div className="bg-amber-50 rounded-2xl border border-amber-200/30 p-4 space-y-2">
        <div className="flex items-center gap-2">
          <Star className="w-4 h-4 text-amber-600" />
          <h4 className="text-[10px] font-sans font-bold text-amber-700 uppercase tracking-wide">Day-of Point of Contact</h4>
        </div>
        <p className="text-[10px] font-sans text-amber-600">This person is who vendors and guests can contact on the day.</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <Inp label="Navn" value={dayOfContact.name} onChange={v => updateDayOfContact('name', v)} placeholder="Contact person" />
          <Inp label="Phone" value={dayOfContact.phone} onChange={v => updateDayOfContact('phone', v)} placeholder="Phone" />
          <Inp label="Role" value={dayOfContact.role} onChange={v => updateDayOfContact('role', v)} placeholder="e.g. best man, planner" />
        </div>
      </div>

      {/* Stats */}
      {tasks.length > 0 && (
        <div className="flex items-center gap-4 bg-white rounded-xl border border-stone-100 p-3">
          <div className="text-center flex-1">
            <p className="text-sm font-sans font-bold text-stone-700">{tasks.length}</p>
            <p className="text-[9px] font-sans text-stone-400 uppercase">Totalt</p>
          </div>
          <div className="text-center flex-1">
            <p className="text-sm font-sans font-bold text-blue-600">{assignedCount}</p>
            <p className="text-[9px] font-sans text-stone-400 uppercase">Assigned</p>
          </div>
          <div className="text-center flex-1">
            <p className="text-sm font-sans font-bold text-green-600">{completedCount}</p>
            <p className="text-[9px] font-sans text-stone-400 uppercase">Completed</p>
          </div>
          <div className="flex-1">
            <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
              <div className="h-full bg-green-500 rounded-full transition-all" style={{ width: `${tasks.length > 0 ? (completedCount / tasks.length) * 100 : 0}%` }} />
            </div>
          </div>
        </div>
      )}

      {/* Tasks by category */}
      {Object.entries(byCategory).map(([cat, catTasks]) => (
        <div key={cat}>
          <p className="text-[10px] font-sans font-bold text-stone-500 uppercase tracking-wide mb-1.5">{cat}</p>
          <div className="space-y-1.5">
            {catTasks.map(t => {
              const isExpanded = expanded === t._idx;
              const statusCfg = STATUS_OPTIONS.find(s => s.value === t.status) || STATUS_OPTIONS[0];
              return (
                <div key={t.id} className={`border rounded-xl transition-all ${isExpanded ? 'border-primary/30 bg-white' : 'border-stone-200'}`}>
                  <div className="flex items-center gap-2 p-2.5 cursor-pointer" onClick={() => setExpanded(isExpanded ? null : t._idx)}>
                    <button type="button" onClick={e => {
                      e.stopPropagation();
                      const nextStatus = t.status === 'pending' ? 'assigned' : t.status === 'assigned' ? 'done' : 'pending';
                      updateTask(t._idx, 'status', nextStatus);
                    }} className={`w-5 h-5 rounded-md border flex items-center justify-center transition-all ${
                      t.status === 'done' ? 'bg-green-500 border-green-500' : t.status === 'assigned' ? 'bg-blue-100 border-blue-300' : 'border-stone-300'
                    }`}>
                      {t.status === 'done' && <Check className="w-3 h-3 text-white" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs font-sans font-medium truncate ${t.status === 'done' ? 'text-stone-400 line-through' : 'text-stone-700'}`}>{t.task || 'New task'}</p>
                      {t.responsible && <p className="text-[9px] font-sans text-stone-400">{t.responsible} {t.time ? `• ${t.time}` : ''}</p>}
                    </div>
                    <span className={`text-[8px] font-sans font-bold px-1.5 py-0.5 rounded-full ${statusCfg.color}`}>{statusCfg.label}</span>
                    <button type="button" onClick={e => { e.stopPropagation(); removeTask(t._idx); }} className="text-stone-300 hover:text-red-500 p-0.5"><Trash2 className="w-3 h-3" /></button>
                  </div>
                  {isExpanded && (
                    <div className="px-3 pb-3 border-t border-stone-100 space-y-2 pt-2">
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <Inp label="Task" value={t.task} onChange={v => updateTask(t._idx, 'task', v)} placeholder="Hva?" />
                        <Inp label="Responsible" value={t.responsible} onChange={v => updateTask(t._idx, 'responsible', v)} placeholder="Hvem?" />
                        <Inp label="Phone" value={t.phone} onChange={v => updateTask(t._idx, 'phone', v)} placeholder="Phone" />
                        <Inp label="Time" value={t.time} onChange={v => updateTask(t._idx, 'time', v)} placeholder="Kl." />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-[9px] font-sans font-semibold text-stone-500 uppercase mb-0.5 block">Kategori</label>
                          <input value={t.category} onChange={e => updateTask(t._idx, 'category', e.target.value)} placeholder="Kategori"
                            className="w-full text-xs font-sans px-2.5 py-1.5 border border-stone-200 rounded-lg bg-white focus:border-primary focus:outline-none" />
                        </div>
                        <Inp label="Notes" value={t.notes} onChange={v => updateTask(t._idx, 'notes', v)} placeholder="Evt. detaljer" />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Add task */}
      <button type="button" onClick={addTask}
        className="w-full border-2 border-dashed border-stone-200 rounded-xl p-2.5 flex items-center justify-center gap-2 text-stone-400 hover:border-stone-300 hover:text-stone-500 transition-all text-xs font-sans font-medium">
        <Plus className="w-3.5 h-3.5" /> Add task
      </button>
    </div>
  );
};
